// GraphQL Queries and Mutations
import { gql, useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TFunction } from "i18next";
import { Eye, EyeOff, Palette, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ServiceMatrixOptionsForm } from "@/domains/menu/service-detail/components/forms/service-matrix-options-form";
import { ServicePreviewCard } from "@/domains/menu/services/components/cards/service-preview-card";
import type { PricingMode, Service, ServiceCategory } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { getSalonCurrencySymbol } from "@/lib/utils/currency";
import { generateServiceCode } from "../../utils";

// Import the main services query to update cache
const SERVICES_WITH_CATEGORIES_QUERY = gql`
  query ServicesWithCategoriesForDialog($salonId: ID!) {
    serviceCategories(salonId: $salonId) {
      id
      name
      description
      color
      icon
      sortOrder
      isArchived
      allowOnlineBooking
      createdAt
      updatedAt
    }
    services(salonId: $salonId) {
      id
      name
      description
      serviceCode
      duration
      pricingMode
      basePrice
      isActive
      allowOnlineBooking
      createdAt
      updatedAt
      category {
        id
        name
        color
      }
      displayPrice {
        mode
        fixedPrice
        priceRange {
          minPrice
          maxPrice
        }
        requiresQuote
      }
      serviceOptionGroups {
        id
        name
        selectionType
        options {
          id
          optionName
          priceModifier
          durationModifier
        }
      }
    }
  }
`;

const SERVICE_CATEGORIES_FOR_CREATE_QUERY = gql`
  query ServiceCategoriesForCreate($salonId: ID!) {
    serviceCategories(salonId: $salonId) {
      id
      name
      color
      isArchived
      allowOnlineBooking
    }
  }
`;

const CREATE_SERVICE_WITH_CATEGORY_MUTATION = gql`
  mutation CreateServiceWithCategory(
    $salonId: ID!
    $input: CreateServiceWithCategoryInput!
  ) {
    createServiceWithCategory(salonId: $salonId, input: $input) {
      id
      name
      description
      serviceCode
      duration
      pricingMode
      basePrice
      allowOnlineBooking
      isActive
      category {
        id
        name
        color
      }
    }
  }
`;

// Form validation schema - will be created with translated messages
const createServiceFormSchema = (t: TFunction<"services">) =>
  z
    .object({
      templateId: z.string().nullable().optional(),
      name: z
        .string()
        .min(1, t("dialog.validation.serviceNameRequired"))
        .max(100, t("dialog.validation.nameMaxLength")),
      description: z.string().optional(),
      categoryType: z.enum(["existing", "new"]),
      categoryId: z.string().optional(),
      newCategoryName: z.string().optional(),
      newCategoryColor: z.string().optional(),
      duration: z
        .number()
        .min(0, t("dialog.validation.durationMin"))
        .max(480, t("dialog.validation.durationMax")),
      pricingMode: z.enum(["NO_PRICE", "SINGLE_PRICE", "MATRIX_PRICE"]),
      pricing: z.object({ pricingMode: z.string() }).optional(),
      basePrice: z.number().min(0, t("dialog.validation.priceNonNegative")).optional(),
      allowOnlineBooking: z.boolean(),
      optionGroups: z
        .array(
          z.object({
            id: z.string().optional(),
            name: z.string(),
            selectionType: z.enum(["SINGLE", "MULTIPLE"]),
            minSelections: z.number(),
            maxSelections: z.number().nullable().optional(),
            sortOrder: z.number(),
            options: z.array(
              z.object({
                id: z.string().optional(),
                optionName: z.string(),
                priceModifier: z.number(),
                durationModifier: z.number(),
                isDefault: z.boolean(),
                sortOrder: z.number(),
              })
            ),
          })
        )
        .optional(),
    })
    .refine(
      (data) => {
        if (data.categoryType === "existing") {
          return data.categoryId && data.categoryId.length > 0;
        }
        if (data.categoryType === "new") {
          return data.newCategoryName && data.newCategoryName.trim().length > 0;
        }
        return false;
      },
      {
        message: t("dialog.validation.categoryRequired"),
        path: ["categoryId"], // Point error to categoryId field for UI consistency
      }
    );

type ServiceFormData = z.infer<ReturnType<typeof createServiceFormSchema>>;

interface CreateServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salonId: string;
  defaultCategoryId?: string;
  onSuccess?: () => void;
}

export function CreateServiceDialog({
  open,
  onOpenChange,
  salonId,
  defaultCategoryId,
  onSuccess,
}: CreateServiceDialogProps) {
  const { t } = useTranslation("services");
  const currency = useSalonStore((state) => state.currentSalon?.currency);
  const [selectedPricingMode, setSelectedPricingMode] = useState<PricingMode>("SINGLE_PRICE");

  // Get currency symbol using built-in Intl API
  const currencySymbol = getSalonCurrencySymbol(currency);
  const [createMoreMode, setCreateMoreMode] = useState(false);
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);

  const form = useForm<ServiceFormData>({
    resolver: zodResolver(createServiceFormSchema(t)),
    defaultValues: {
      templateId: null,
      name: "",
      description: "",
      categoryType: defaultCategoryId ? "existing" : "existing",
      categoryId: defaultCategoryId || "",
      newCategoryName: "",
      newCategoryColor: "#3b82f6",
      duration: 60,
      pricingMode: "SINGLE_PRICE",
      pricing: { pricingMode: "SINGLE_PRICE" },
      basePrice: 0,
      allowOnlineBooking: true,
      optionGroups: [],
    },
  });

  // Queries
  const { data: categoriesData, loading: categoriesLoading } = useQuery(
    SERVICE_CATEGORIES_FOR_CREATE_QUERY,
    {
      variables: { salonId },
      skip: !salonId,
    }
  );

  // Mutations
  const [_lastCreatedCategoryId, setLastCreatedCategoryId] = useState<string | null>(null);

  const [createServiceWithCategory, { loading: createLoading }] = useMutation(
    CREATE_SERVICE_WITH_CATEGORY_MUTATION,
    {
      update: (cache, { data }) => {
        const createdService = data?.createServiceWithCategory;

        if (!createdService) return;

        try {
          // Update the main services and categories cache
          const existingMainData = cache.readQuery({
            query: SERVICES_WITH_CATEGORIES_QUERY,
            variables: { salonId },
          }) as { serviceCategories: ServiceCategory[]; services: Service[] } | null;

          if (existingMainData) {
            const updatedCategories = [...existingMainData.serviceCategories];
            const updatedServices = [...existingMainData.services];

            // Add new category if it was created
            if (createdService.category && form.getValues("categoryType") === "new") {
              const categoryExists = updatedCategories.some(
                (cat: ServiceCategory) => cat.id === createdService.category.id
              );

              if (!categoryExists) {
                updatedCategories.push(createdService.category);
              }
            }

            // Add the new service to the services list
            updatedServices.push(createdService);

            // Write back to cache
            cache.writeQuery({
              query: SERVICES_WITH_CATEGORIES_QUERY,
              variables: { salonId },
              data: {
                serviceCategories: updatedCategories,
                services: updatedServices,
              },
            });
          }

          // Also update the categories-only cache for the create dialog
          if (createdService?.category && form.getValues("categoryType") === "new") {
            const existingCategoriesData = cache.readQuery({
              query: SERVICE_CATEGORIES_FOR_CREATE_QUERY,
              variables: { salonId },
            }) as { serviceCategories: ServiceCategory[] } | null;

            if (existingCategoriesData?.serviceCategories) {
              const categoryExists = existingCategoriesData.serviceCategories.some(
                (cat: ServiceCategory) => cat.id === createdService.category.id
              );

              if (!categoryExists) {
                cache.writeQuery({
                  query: SERVICE_CATEGORIES_FOR_CREATE_QUERY,
                  variables: { salonId },
                  data: {
                    serviceCategories: [
                      ...existingCategoriesData.serviceCategories,
                      createdService.category,
                    ],
                  },
                });
              }
            }
          }
        } catch {
          // Don't throw - let the mutation complete successfully
        }
      },
      onCompleted: async (data) => {
        const createdService = data?.createServiceWithCategory;

        if (createMoreMode) {
          toast.success(t("dialog.messages.serviceCreatedReadyNext"));
        } else {
          toast.success(t("dialog.messages.serviceCreatedSuccess"));
        }

        if (createMoreMode) {
          // Smart reset: preserve pricing mode and category, reset other fields
          const currentPricingMode = form.getValues("pricingMode");

          // Use the newly created category if it was created inline, otherwise use the current form value
          const categoryToPreserve = createdService?.category?.id || form.getValues("categoryId");

          form.reset({
            templateId: null,
            name: "",
            description: "",
            categoryType: "existing",
            categoryId: categoryToPreserve,
            newCategoryName: "",
            newCategoryColor: "#3b82f6",
            duration: 60,
            pricingMode: currentPricingMode,
            pricing: { pricingMode: currentPricingMode },
            basePrice: currentPricingMode === "SINGLE_PRICE" ? 0 : undefined,
            allowOnlineBooking: true,
            optionGroups: [],
          });
          setSelectedPricingMode(currentPricingMode);
          setLastCreatedCategoryId(null);
        } else {
          onOpenChange(false);
          form.reset({
            templateId: null,
            name: "",
            description: "",
            categoryType: "existing",
            categoryId: defaultCategoryId || "",
            newCategoryName: "",
            newCategoryColor: "#3b82f6",
            duration: 60,
            pricingMode: "SINGLE_PRICE",
            pricing: { pricingMode: "SINGLE_PRICE" },
            basePrice: 0,
            allowOnlineBooking: true,
            optionGroups: [],
          });
        }
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(t("dialog.messages.createServiceError", { error: error.message }));
      },
    }
  );

  const loading = createLoading;
  const categories = categoriesData?.serviceCategories || [];

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        templateId: null,
        name: "",
        description: "",
        categoryType: "existing",
        categoryId: defaultCategoryId || "",
        newCategoryName: "",
        newCategoryColor: "#3b82f6",
        duration: 60,
        pricingMode: "SINGLE_PRICE",
        pricing: { pricingMode: "SINGLE_PRICE" },
        basePrice: 0,
        allowOnlineBooking: true,
        optionGroups: [],
      });
      setSelectedPricingMode("SINGLE_PRICE");
      setCreateMoreMode(false);
    }
  }, [open, defaultCategoryId, form]);

  const onSubmit = async (data: ServiceFormData) => {
    try {
      // Auto-generate the service code from the name. The backend resolves
      // any collision by suffixing `_2`, `_3`, ... within the catalog.
      const serviceCode = generateServiceCode(data.name);

      // Build the input object for the compound mutation
      const input: any = {
        templateId: data.templateId ?? null,
        name: data.name,
        description: data.description,
        serviceCode,
        duration: data.duration,
        pricingMode: data.pricingMode,
        basePrice:
          data.pricingMode === "SINGLE_PRICE" && data.basePrice
            ? Math.round(data.basePrice * 100)
            : undefined,
        allowOnlineBooking: data.allowOnlineBooking,
        optionGroups:
          data.pricingMode === "MATRIX_PRICE" && data.optionGroups?.length
            ? data.optionGroups.map((group) => ({
                name: group.name,
                selectionType: group.selectionType,
                minSelections: group.minSelections,
                maxSelections: group.maxSelections ?? undefined,
                sortOrder: group.sortOrder,
                options: group.options.map((opt) => ({
                  optionName: opt.optionName,
                  priceModifier: opt.priceModifier,
                  durationModifier: opt.durationModifier,
                  isDefault: opt.isDefault,
                  sortOrder: opt.sortOrder,
                })),
              }))
            : undefined,
      };

      // Add category information based on type
      if (data.categoryType === "existing") {
        input.categoryId = data.categoryId;
      } else if (data.categoryType === "new" && data.newCategoryName?.trim()) {
        input.newCategory = {
          name: data.newCategoryName.trim(),
          color: data.newCategoryColor || "#3b82f6",
          allowOnlineBooking: true,
          sortOrder: 0,
        };
        // Set lastCreatedCategoryId to track for create more mode
        setLastCreatedCategoryId("new-category-placeholder");
      }

      await createServiceWithCategory({
        variables: {
          salonId,
          input,
        },
      });
    } catch (_error) {
      // Error handled by mutation onError callback
    }
  };

  const handlePricingModeChange = (value: string) => {
    const mode = value as PricingMode;
    setSelectedPricingMode(mode);
    form.setValue("pricingMode", mode);
    form.setValue("pricing", { pricingMode: mode });

    // Reset base price when changing to NO_PRICE or MATRIX_PRICE
    if (mode === "NO_PRICE" || mode === "MATRIX_PRICE") {
      form.setValue("basePrice", undefined);
    }
  };

  // Get selected template details for display
  const selectedCategory = categories.find((cat) => cat.id === form.watch("categoryId"));

  const handleCategoryChange = (value: string) => {
    if (value === "create-new") {
      form.setValue("categoryType", "new", { shouldValidate: true });
      form.setValue("categoryId", "", { shouldValidate: true });
      form.setValue("newCategoryName", "");
      form.setValue("newCategoryColor", "#3b82f6");
    } else {
      form.setValue("categoryType", "existing", { shouldValidate: true });
      form.setValue("categoryId", value, { shouldValidate: true });
      form.setValue("newCategoryName", "");
      form.setValue("newCategoryColor", "#3b82f6");
    }
  };

  const handleDialogOpenChange = (next: boolean) => {
    if (!next && form.formState.isDirty) {
      setShowDiscardAlert(true);
      return;
    }
    onOpenChange(next);
  };

  const handleConfirmDiscard = () => {
    form.reset({
      templateId: null,
      name: "",
      description: "",
      categoryType: "existing",
      categoryId: defaultCategoryId || "",
      newCategoryName: "",
      newCategoryColor: "#3b82f6",
      duration: 60,
      pricingMode: "SINGLE_PRICE",
      pricing: { pricingMode: "SINGLE_PRICE" },
      basePrice: 0,
      allowOnlineBooking: true,
      optionGroups: [],
    });
    setSelectedPricingMode("SINGLE_PRICE");
    setShowDiscardAlert(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-[600px] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 flex-none">
            <DialogTitle>{t("dialog.title")}</DialogTitle>
            <DialogDescription>{t("dialog.description")}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("dialog.fields.serviceName")}</Label>
                    <Input
                      id="name"
                      placeholder={t("dialog.placeholders.serviceName")}
                      {...form.register("name")}
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">{t("dialog.fields.description")}</Label>
                    <Textarea
                      id="description"
                      placeholder={t("dialog.placeholders.description")}
                      className="resize-none"
                      rows={3}
                      {...form.register("description")}
                    />
                    <p className="text-sm text-muted-foreground">{t("dialog.hints.description")}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="categoryId">{t("dialog.fields.category")}</Label>
                    <Select
                      onValueChange={handleCategoryChange}
                      value={
                        form.watch("categoryType") === "new"
                          ? "create-new"
                          : form.watch("categoryId")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("selectServiceCategory")}>
                          {form.watch("categoryType") === "new" ? (
                            <div className="flex items-center gap-2">
                              <Plus className="w-3 h-3" />
                              {t("dialog.actions.createNewCategory")}
                            </div>
                          ) : selectedCategory ? (
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: selectedCategory.color }}
                              />
                              {selectedCategory.name}
                            </div>
                          ) : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="create-new">
                          <div className="flex items-center gap-2">
                            <Plus className="w-3 h-3" />
                            {t("dialog.actions.createNewCategory")}
                          </div>
                        </SelectItem>
                        {categoriesLoading ? (
                          <SelectItem value="" disabled>
                            {t("dialog.status.loadingCategories")}
                          </SelectItem>
                        ) : categories.length === 0 ? (
                          <SelectItem value="" disabled>
                            {t("dialog.status.noCategoriesAvailable")}
                          </SelectItem>
                        ) : (
                          categories.map((category: ServiceCategory) => (
                            <SelectItem key={category.id} value={category.id}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: category.color }}
                                />
                                {category.name}
                                {!category.allowOnlineBooking && (
                                  <Badge variant="outline" className="text-xs ml-1">
                                    {t("dialog.labels.private")}
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.categoryId && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.categoryId.message}
                      </p>
                    )}

                    {/* Inline Category Creation */}
                    {form.watch("categoryType") === "new" && (
                      <div className="p-4 border rounded-lg bg-blue-50/50 space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                          <Plus className="w-4 h-4" />
                          {t("dialog.actions.createNewCategory")}
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-2 space-y-1">
                            <Label htmlFor="newCategoryName" className="text-xs">
                              {t("dialog.fields.categoryName")}
                            </Label>
                            <Input
                              id="newCategoryName"
                              placeholder={t("dialog.placeholders.categoryName")}
                              value={form.watch("newCategoryName") || ""}
                              onChange={(e) =>
                                form.setValue("newCategoryName", e.target.value, {
                                  shouldValidate: true,
                                })
                              }
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="newCategoryColor" className="text-xs">
                              {t("dialog.fields.color")}
                            </Label>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded border cursor-pointer"
                                style={{
                                  backgroundColor: form.watch("newCategoryColor") || "#3b82f6",
                                }}
                                onClick={() => {
                                  const colors = [
                                    "#3b82f6",
                                    "#10b981",
                                    "#8b5cf6",
                                    "#f59e0b",
                                    "#ef4444",
                                    "#06b6d4",
                                  ];
                                  const currentColor = form.watch("newCategoryColor") || "#3b82f6";
                                  const currentIndex = colors.indexOf(currentColor);
                                  const nextIndex = (currentIndex + 1) % colors.length;
                                  form.setValue("newCategoryColor", colors[nextIndex]);
                                }}
                              />
                              <Palette className="w-3 h-3 text-muted-foreground" />
                            </div>
                          </div>
                        </div>

                        <p className="text-xs text-blue-600">
                          {t("dialog.hints.newCategoryCreation")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Duration and Timing */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="duration">{t("dialog.fields.duration")}</Label>
                      <Input
                        id="duration"
                        type="number"
                        min="0"
                        max="480"
                        step="5"
                        {...form.register("duration", {
                          setValueAs: (value) => (value === "" ? 0 : Number(value)),
                        })}
                      />
                      <p className="text-sm text-muted-foreground">{t("dialog.hints.duration")}</p>
                      {form.formState.errors.duration && (
                        <p className="text-sm text-destructive">
                          {form.formState.errors.duration.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing Configuration */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("dialog.fields.pricingMode")}</Label>
                    <Tabs value={form.watch("pricingMode")} onValueChange={handlePricingModeChange}>
                      <TabsList className="w-full">
                        <TabsTrigger value="SINGLE_PRICE" className="flex-1">
                          {t("dialog.pricing.fixedPrice.title")}
                        </TabsTrigger>
                        <TabsTrigger value="MATRIX_PRICE" className="flex-1">
                          {t("dialog.pricing.configurablePrice.title")}
                        </TabsTrigger>
                        <TabsTrigger value="NO_PRICE" className="flex-1">
                          {t("dialog.pricing.noPrice.title")}
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>

                  {selectedPricingMode === "SINGLE_PRICE" && (
                    <div className="space-y-2">
                      <Label htmlFor="basePrice">{t("dialog.fields.servicePrice")}</Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                          <span className="text-muted-foreground text-sm font-medium">
                            {currencySymbol}
                          </span>
                        </div>
                        <Input
                          id="basePrice"
                          type="number"
                          min="0"
                          step="0.01"
                          className="pl-10"
                          placeholder="0"
                          {...form.register("basePrice", { valueAsNumber: true })}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t("dialog.hints.fixedPrice")}
                      </p>
                      {form.formState.errors.basePrice && (
                        <p className="text-sm text-destructive">
                          {form.formState.errors.basePrice.message}
                        </p>
                      )}
                    </div>
                  )}

                  {selectedPricingMode === "MATRIX_PRICE" && <ServiceMatrixOptionsForm />}

                  {selectedPricingMode === "NO_PRICE" && (
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-700">
                        <strong>{t("dialog.pricing.quoteRequired.title")}:</strong>{" "}
                        {t("dialog.pricing.quoteRequired.info")}
                      </p>
                    </div>
                  )}
                </div>

                {/* Service Settings */}
                <div className="space-y-4">
                  <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">{t("dialog.fields.allowOnlineBooking")}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t("dialog.hints.allowOnlineBooking")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {form.watch("allowOnlineBooking") ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      <Switch
                        checked={form.watch("allowOnlineBooking")}
                        onCheckedChange={(checked) => form.setValue("allowOnlineBooking", checked)}
                      />
                    </div>
                  </div>
                </div>

                {/* Customer-facing preview */}
                <ServicePreviewCard
                  name={form.watch("name") || ""}
                  description={form.watch("description")}
                  duration={form.watch("duration") || 0}
                  pricingMode={form.watch("pricingMode")}
                  basePrice={form.watch("basePrice")}
                  optionGroups={form.watch("optionGroups")?.map((g) => ({
                    name: g.name,
                    selectionType: g.selectionType,
                    options: g.options.map((o) => ({
                      optionName: o.optionName,
                      priceModifier: o.priceModifier / 100,
                    })),
                  }))}
                  displayMode={
                    (form.watch("basicInfo.storefrontDisplayMode" as never) as unknown as
                      | "TABLE"
                      | "REGULAR"
                      | null
                      | undefined) ?? null
                  }
                />
              </form>
            </Form>
          </div>

          <DialogFooter className="flex-none border-t px-6 py-4">
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="create-more-mode"
                  checked={createMoreMode}
                  onCheckedChange={setCreateMoreMode}
                />
                <Label htmlFor="create-more-mode" className="text-sm">
                  {t("dialog.actions.createMoreServices")}
                </Label>
              </div>
              <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                {loading
                  ? t("dialog.actions.creating")
                  : createMoreMode
                    ? t("dialog.actions.createAndContinue")
                    : t("dialog.actions.createService")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dialog.confirmClose.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dialog.confirmClose.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("dialog.confirmClose.continue")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard}>
              {t("dialog.confirmClose.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
